import { MINUTE } from '@lib/utils/time';
import { useCallback, useEffect, useRef, useState } from 'react';

const SITE_KEY = process.env.TURNSTILE_SITE_KEY ?? '';

interface UseTurnstileOptions {
  containerId: string;
  autoRender?: boolean;
}

interface UseTurnstileReturn {
  token: string | null;
  error: string | null;
  reset: () => void;
  getToken: () => Promise<string>;
  renderWidget: () => void;
  removeWidget: () => void;
}

export function useTurnstile({
  containerId,
  autoRender = true,
}: UseTurnstileOptions): UseTurnstileReturn {
  const [token, setToken] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const widgetIdRef = useRef<string | null>(null);
  const pendingResolveRef = useRef<((token: string) => void) | null>(null);
  const pendingRejectRef = useRef<((reason: Error) => void) | null>(null);
  const pendingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const settlePending = useCallback(() => {
    if (pendingTimeoutRef.current) {
      clearTimeout(pendingTimeoutRef.current);
      pendingTimeoutRef.current = null;
    }
    pendingResolveRef.current = null;
    pendingRejectRef.current = null;
  }, []);

  const renderWidget = useCallback(() => {
    if (widgetIdRef.current !== null) return;
    if (typeof turnstile === 'undefined') return;

    const container = document.getElementById(containerId);
    if (!container) return;

    setToken(null);
    setError(null);

    widgetIdRef.current = turnstile.render(container, {
      sitekey: SITE_KEY,
      theme: 'dark',
      size: 'flexible',
      // Run the challenge invisibly in the background; only reveal the widget
      // if Cloudflare decides an interactive challenge is required (rare).
      appearance: 'interaction-only',
      callback: (t: string) => {
        setToken(t);
        setError(null);
        pendingResolveRef.current?.(t);
      },
      'error-callback': () => {
        setError('Verification failed. Please try again.');
        setToken(null);
        pendingRejectRef.current?.(new Error('Turnstile verification failed'));
      },
      'expired-callback': () => {
        setToken(null);
        // Re-run the challenge so a fresh token is ready. Without this, after a
        // failed auto-refresh the next getToken() would await a challenge that
        // is no longer running and hang until the 60s timeout. (Cloudflare
        // recommends reset() in the expired callback for programmatic flows.)
        if (widgetIdRef.current !== null && typeof turnstile !== 'undefined') {
          turnstile.reset(widgetIdRef.current);
        }
      },
    });
  }, [containerId]);

  const removeWidget = useCallback(() => {
    if (widgetIdRef.current !== null && typeof turnstile !== 'undefined') {
      turnstile.remove(widgetIdRef.current);
      widgetIdRef.current = null;
    }
    settlePending();
    setToken(null);
    setError(null);
  }, [settlePending]);

  const reset = useCallback(() => {
    if (widgetIdRef.current !== null && typeof turnstile !== 'undefined') {
      turnstile.reset(widgetIdRef.current);
    }
    setToken(null);
    setError(null);
  }, []);

  const getToken = useCallback((): Promise<string> => {
    if (token) return Promise.resolve(token);

    return new Promise<string>((resolve, reject) => {
      pendingTimeoutRef.current = setTimeout(() => {
        settlePending();
        reject(new Error('Turnstile token timed out'));
      }, MINUTE);
      pendingResolveRef.current = (t: string) => {
        settlePending();
        resolve(t);
      };
      pendingRejectRef.current = (reason: Error) => {
        settlePending();
        reject(reason);
      };
    });
  }, [token, settlePending]);

  useEffect(() => {
    if (!autoRender) return;

    const tryRender = () => {
      if (typeof turnstile !== 'undefined') {
        renderWidget();
      } else {
        const interval = setInterval(() => {
          if (typeof turnstile !== 'undefined') {
            clearInterval(interval);
            renderWidget();
          }
        }, 200);
        return () => clearInterval(interval);
      }
    };

    const cleanup = tryRender();
    return () => {
      cleanup?.();
      removeWidget();
    };
  }, [autoRender, renderWidget, removeWidget]);

  // Unconditionally tear down a pending getToken() timeout on unmount, even
  // when autoRender is false (the effect above early-returns in that case).
  useEffect(() => () => settlePending(), [settlePending]);

  return { token, error, reset, getToken, renderWidget, removeWidget };
}
